import { useQuery, useQueryClient } from "@tanstack/solid-query";
import type { MetricCatalogAdmin } from "contracts/api/metric/responses";
import { createSignal, For, Show } from "solid-js";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";

// Admin editing of the global metric catalog (public.metrics). Keys and pillars
// are structural (read-only); thresholds, label and description are editable and
// take effect on every website's health rollups.
export function MetricCatalogEditor() {
  const catalog = useQuery(() => ({
    queryKey: ["metric-catalog"],
    queryFn: () => api.listMetricCatalog(),
  }));

  return (
    <section class="admin-section">
      <h4 class="admin-section-title">Metric catalog</h4>
      <Show
        when={catalog.data}
        fallback={<div class="loading">Loading catalog…</div>}
      >
        {(data) => (
          <div class="tbl-wrap">
            <table class="tbl">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th class="td-num">Watch</th>
                  <th class="td-num">Good</th>
                  <th class="td-num">Target</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <For each={data().metrics}>
                  {(metric) => <MetricRow metric={metric} />}
                </For>
              </tbody>
            </table>
          </div>
        )}
      </Show>
    </section>
  );
}

function MetricRow(props: { metric: MetricCatalogAdmin }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = createSignal(props.metric.label);
  const [description, setDescription] = createSignal(props.metric.description);
  const [watch, setWatch] = createSignal(props.metric.watch);
  const [good, setGood] = createSignal(props.metric.good);
  const [target, setTarget] = createSignal(props.metric.target);
  const action = createAsyncAction((cause) =>
    cause instanceof Error ? cause.message : "Save failed",
  );

  const save = () =>
    action.run(async () => {
      await api.updateMetricCatalog(props.metric.key, {
        label: label().trim(),
        pillar: props.metric.pillar,
        target: target(),
        good: good(),
        watch: watch(),
        description: description().trim(),
      });
      await queryClient.invalidateQueries({ queryKey: ["metric-catalog"] });
    });

  return (
    <tr>
      <td>
        <input
          class="editor-input"
          value={label()}
          onInput={(event) => setLabel(event.currentTarget.value)}
        />
        <div class="td-issue-sub mono">
          {props.metric.key} · {props.metric.pillar}
        </div>
        <textarea
          class="editor-input"
          rows="2"
          value={description()}
          onInput={(event) => setDescription(event.currentTarget.value)}
        />
        <Show when={action.error()}>
          <span class="gate-error">{action.error()}</span>
        </Show>
      </td>
      <td class="td-num">
        <ScoreInput value={watch()} onChange={setWatch} />
      </td>
      <td class="td-num">
        <ScoreInput value={good()} onChange={setGood} />
      </td>
      <td class="td-num">
        <ScoreInput value={target()} onChange={setTarget} />
      </td>
      <td>
        <button
          class="btn"
          type="button"
          disabled={action.busy()}
          onClick={save}
        >
          {action.busy() ? "Saving…" : "Save"}
        </button>
      </td>
    </tr>
  );
}

function ScoreInput(props: {
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <input
      class="editor-input mono"
      type="number"
      min="0"
      max="100"
      value={props.value}
      onInput={(event) =>
        props.onChange(
          Math.max(
            0,
            Math.min(100, Number.parseInt(event.currentTarget.value, 10) || 0),
          ),
        )
      }
    />
  );
}
